import { apiClient } from './client'
import { API_ENDPOINTS } from '@/utils/constants'

export interface Tag {
  id: number
  name: string
  color?: string
  owner_id?: number | null
  created_by?: number | null
  created_at?: string
  updated_at?: string
  words_count?: number
}

interface ApiResponse<T> {
  success?: boolean
  message?: string
  data?: T
}

interface TagsListResponse {
  data?: Tag[]
  meta?: {
    current_page?: number
    last_page?: number
    per_page?: number
    total?: number
  }
  current_page?: number
  last_page?: number
  per_page?: number
  total?: number
}

export interface TagsListParams {
  page?: number
  per_page?: number
  search?: string
  system?: boolean
}

const BASE_URL = API_ENDPOINTS.TAGS

function buildQueryString(params: Record<string, unknown>): string {
  const searchParams = new URLSearchParams()
  Object.keys(params).forEach((key) => {
    const value = params[key]
    if (value !== undefined && value !== null && value !== '') {
      searchParams.append(key, String(value))
    }
  })
  return searchParams.toString()
}

export const tagsApi = {
  getList: async (params?: TagsListParams): Promise<{
    data: Tag[]
    current_page: number
    last_page: number
    per_page: number
    total: number
  }> => {
    const query = params ? `?${buildQueryString(params as Record<string, unknown>)}` : ''
    const response = await apiClient.get<Tag[] | TagsListResponse>(
      `${BASE_URL}${query}`
    )

    if (Array.isArray(response.data)) {
      return {
        data: response.data,
        current_page: 1,
        last_page: 1,
        per_page: 20,
        total: response.data.length,
      }
    }

    const raw = response.data as TagsListResponse
    if (raw?.data && Array.isArray(raw.data)) {
      const meta = raw.meta || {}
      return {
        data: raw.data,
        current_page: meta.current_page ?? raw.current_page ?? 1,
        last_page: meta.last_page ?? raw.last_page ?? 1,
        per_page: meta.per_page ?? raw.per_page ?? 20,
        total: meta.total ?? raw.total ?? 0,
      }
    }

    return {
      data: [],
      current_page: 1,
      last_page: 1,
      per_page: 20,
      total: 0,
    }
  },

  getOne: async (id: number): Promise<Tag> => {
    const response = await apiClient.get<ApiResponse<Tag>>(`${BASE_URL}/${id}`)

    if (response.data?.data) {
      return response.data.data
    }

    throw new Error(
      response.data?.message || 'Ошибка загрузки тега'
    )
  },

  search: async (query: string): Promise<Tag[]> => {
    const response = await apiClient.get<ApiResponse<Tag[]>>(
      `${BASE_URL}/search`,
      { params: { search: query } }
    )

    if (response.data?.data) {
      return response.data.data
    }

    return []
  },

  create: async (body: { name: string; color?: string }): Promise<Tag> => {
    const response = await apiClient.post<ApiResponse<Tag>>(BASE_URL, body)

    if (response.data?.data) {
      return response.data.data
    }

    throw new Error(
      response.data?.message || 'Ошибка создания тега'
    )
  },

  update: async (
    id: number,
    body: { name?: string; color?: string }
  ): Promise<Tag> => {
    const response = await apiClient.put<ApiResponse<Tag>>(
      `${BASE_URL}/${id}`,
      body
    )

    if (response.data?.data) {
      return response.data.data
    }

    throw new Error(
      response.data?.message || 'Ошибка обновления тега'
    )
  },

  delete: async (id: number): Promise<void> => {
    const response = await apiClient.delete<ApiResponse<unknown>>(
      `${BASE_URL}/${id}`
    )

    if (response.data?.success === false) {
      throw new Error(
        (response.data as { message?: string })?.message || 'Ошибка удаления тега'
      )
    }
  },
}
